<script setup lang="ts">
import { Product } from "~/utils/Product";
import { useModyApi } from '~/middelware/ModyApi';

useSeoMeta({
  title: "Sortiment",
  ogTitle: "Sortiment"
})

const searchString = ref("")
const api = useModyApi();
const searchResults = ref<Product[]>([])
const searchOpen = ref(false)
const searchLoading = ref(false)
const searchError = ref(false)
const hasSearched = ref(false)
const router = useRouter()
const route = useRoute()
const isShopIndex = computed(() => route.path === '/shop' || route.path === '/shop/')
const showSearch = computed(() => route.path !== '/shop/request')
const inputTextOutside = ref<any>(null)
const inputTextModal = ref<any>(null)
const normalizedSearch = computed(() => searchString.value.trim().replace(/\s+/g, " "))
const searchResultLabel = computed(() => {
  const count = searchResults.value.length
  return `${count} Treffer`
})
let searchTimer: ReturnType<typeof setTimeout> | undefined
let requestSequence = 0
let previousBodyOverflow = ""

function clearPendingSearch(){
  if(searchTimer){
    clearTimeout(searchTimer)
    searchTimer = undefined
  }
}

async function executeSearch(query: string, sequence: number){
  try{
    const results = await api.searchProducts(query)
    if(sequence !== requestSequence) return
    searchResults.value = results
    searchError.value = false
  }
  catch{
    if(sequence !== requestSequence) return
    searchResults.value = []
    searchError.value = true
  }
  finally{
    if(sequence === requestSequence){
      searchLoading.value = false
      hasSearched.value = true
    }
  }
}

function scheduleSearch(delay = 280){
  const query = normalizedSearch.value
  requestSequence++
  clearPendingSearch()
  searchResults.value = []
  searchError.value = false
  hasSearched.value = false

  if(query.length < 3){
    searchLoading.value = false
    return
  }

  searchLoading.value = true
  const sequence = requestSequence
  searchTimer = setTimeout(() => executeSearch(query, sequence), delay)
}

function openSearch(){
  if(normalizedSearch.value.length < 3) return
  searchOpen.value = true
}

async function closeSearch(restoreFocus = true){
  searchOpen.value = false
  if(restoreFocus){
    await nextTick()
    inputTextOutside.value?.$el?.focus()
  }
}

function retrySearch(){
  scheduleSearch(0)
}

function handleGlobalSearchKeydown(event: KeyboardEvent){
  if(searchOpen.value && event.key === "Escape"){
    event.preventDefault()
    closeSearch()
  }
}

function trapSearchFocus(event: KeyboardEvent){
  const dialog = event.currentTarget as HTMLElement
  const focusableElements = Array.from(dialog.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
  )).filter(element => element.offsetParent !== null)
  if(focusableElements.length === 0) return

  const firstElement = focusableElements[0]
  const lastElement = focusableElements[focusableElements.length - 1]
  if(event.shiftKey && document.activeElement === firstElement){
    event.preventDefault()
    lastElement.focus()
  }
  else if(!event.shiftKey && document.activeElement === lastElement){
    event.preventDefault()
    firstElement.focus()
  }
}

watch(searchString, () => {
  if(normalizedSearch.value.length >= 3 && !searchOpen.value){
    openSearch()
  }
  scheduleSearch()
})

watch(() => router.currentRoute.value.fullPath, () => {
  searchString.value = ""
  searchOpen.value = false
  searchResults.value = []
})

watch(searchOpen, async (isOpen) => {
  if(!import.meta.client) return
  if(isOpen){
    if(document.body.style.overflow !== "hidden"){
      previousBodyOverflow = document.body.style.overflow
    }
    document.body.style.overflow = "hidden"
    await nextTick()
    inputTextModal.value?.$el?.focus()
  }
  else{
    document.body.style.overflow = previousBodyOverflow
  }
})

onMounted(() => {
  document.addEventListener("keydown", handleGlobalSearchKeydown, true)
})

onBeforeUnmount(() => {
  clearPendingSearch()
  requestSequence++
  if(import.meta.client){
    document.removeEventListener("keydown", handleGlobalSearchKeydown, true)
  }
  if(import.meta.client){
    document.body.style.overflow = previousBodyOverflow
  }
})
</script>
<template>
  <div class="shop-container">
    <PublicPageIntro
      v-if="isShopIndex"
      title="Unser Sortiment"
      description="Finde professionelle Veranstaltungstechnik, praktische Setangebote und passende Lösungen für dein Event."
      compact
    />
    <div v-if="showSearch" class="shop-search-bar">
      <label for="shop-search">Sortiment durchsuchen</label>
      <InputGroup class="shop-search-input">
      <InputGroupAddon>
        <i class="pi pi-search"></i>
      </InputGroupAddon>
      <InputText
        id="shop-search"
        ref="inputTextOutside"
        v-model="searchString"
        placeholder="Produkt suchen"
        autocomplete="off"
        @keydown.enter.prevent="openSearch"
      />
    </InputGroup>
    </div>
    <Teleport to="body">
      <div v-if="searchOpen" class="search-backdrop" @mousedown.self="closeSearch()">
        <section
          class="search-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="search-dialog-title"
          @keydown.esc.stop.prevent="closeSearch()"
          @keydown.tab="trapSearchFocus"
        >
          <header class="search-dialog__header">
            <div>
              <h2 id="search-dialog-title">Passende Technik finden</h2>
              <span>Durchsuche Produkte und Setangebote nach Name oder Einsatzzweck.</span>
            </div>
            <button type="button" class="search-close" aria-label="Suche schließen" @click="closeSearch()">
              <i class="pi pi-times" aria-hidden="true"></i>
            </button>
          </header>

          <div class="search-dialog__controls">
            <InputGroup class="search-dialog__input">
              <InputGroupAddon><i class="pi pi-search" aria-hidden="true"></i></InputGroupAddon>
              <InputText
                id="shop-search-dialog"
                ref="inputTextModal"
                v-model="searchString"
                aria-label="Produkte durchsuchen"
                placeholder="Zum Beispiel DJ Set, Funkmikrofon oder Bühne"
                autocomplete="off"
                @keydown.esc.stop.prevent="closeSearch()"
              />
              <InputGroupAddon v-if="searchLoading"><i class="pi pi-spin pi-spinner" aria-hidden="true"></i></InputGroupAddon>
            </InputGroup>
            <p>Mindestens 3 Zeichen · mehrere Begriffe werden gemeinsam berücksichtigt</p>
          </div>

          <div class="search-dialog__results" aria-live="polite" aria-busy="searchLoading">
            <div v-if="normalizedSearch.length >= 3 && !searchLoading && !searchError && hasSearched" class="search-result-summary">
              <strong>{{ searchResultLabel }}</strong>
              <span>für „{{ normalizedSearch }}“</span>
            </div>

            <div v-if="normalizedSearch.length < 3" class="search-state">
              <i class="pi pi-search" aria-hidden="true"></i>
              <h3>Was suchst du?</h3>
              <p>Gib mindestens drei Zeichen ein, um das Sortiment zu durchsuchen.</p>
            </div>

            <div v-else-if="searchLoading" class="search-result-grid" aria-label="Suchergebnisse werden geladen">
              <div v-for="index in 6" :key="index" class="search-result-skeleton">
                <Skeleton height="185px" />
                <Skeleton width="45%" height="0.7rem" />
                <Skeleton width="78%" height="1.1rem" />
                <Skeleton width="35%" height="0.85rem" />
              </div>
            </div>

            <div v-else-if="searchError" class="search-state search-state--error" role="alert">
              <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
              <h3>Die Suche konnte nicht geladen werden.</h3>
              <p>Bitte versuche es noch einmal.</p>
              <Button label="Erneut suchen" icon="pi pi-refresh" outlined @click="retrySearch" />
            </div>

            <div v-else-if="hasSearched && searchResults.length === 0" class="search-state">
              <i class="pi pi-search" aria-hidden="true"></i>
              <h3>Keine passenden Produkte gefunden</h3>
              <p>Versuche einen allgemeineren Begriff oder beschreibe dein Event direkt in einer Anfrage.</p>
              <NuxtLink to="/shop/request" @click="closeSearch(false)">Unverbindlich anfragen <i class="pi pi-arrow-right" aria-hidden="true"></i></NuxtLink>
            </div>

            <div v-else-if="searchResults.length" class="search-result-grid">
              <ShopSearchResultItem v-for="product in searchResults" :key="product.id" :product="product" />
            </div>
          </div>
        </section>
      </div>
    </Teleport>
    <NuxtPage />
  </div>
</template>
<style scoped lang="scss">

.shop-search-bar{
  width: min(100%, 1120px);
  margin: 0 auto;
  padding: 1.5rem 1.25rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  label{
    color: rgb(168 162 158);
    font-size: 0.88rem;
    font-weight: 650;
  }
}

.shop-search-input{
  width: min(100%, 320px);
  height: 42px;
}

@media (max-width: 639px){
  .shop-search-bar{
    align-items: stretch;
    flex-direction: column;
  }

  .shop-search-input{
    width: 100%;
  }
}

.search-backdrop{
  position: fixed;
  inset: 0;
  z-index: 3000;
  padding: 1rem;
  display: grid;
  place-items: center;
  background: rgba(2, 2, 3, 0.88);
}

.search-dialog{
  width: min(100%, 1240px);
  height: min(calc(100dvh - 2rem), 920px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: $public-radius-large;
  color: white;
  background: #111214;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.48);
}

.search-dialog__header{
  padding: clamp(1.1rem, 2.5vw, 1.6rem) clamp(1.25rem, 3vw, 2rem) 0.85rem;
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  justify-content: space-between;

  h2{
    margin: 0;
    font-size: clamp(1.55rem, 3vw, 2.2rem);
    font-weight: 600;
    line-height: 1.05;
    letter-spacing: -0.035em;
  }

  span{
    margin-top: 0.65rem;
    display: block;
    color: rgb(168 162 158);
    font-size: 0.88rem;
  }
}

.search-close{
  width: 44px;
  height: 44px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: $public-radius-control;
  color: white;
  background: rgba(255, 255, 255, 0.055);
  cursor: pointer;

  &:hover{
    border-color: rgba(227, 151, 55, 0.55);
    background: rgba(227, 151, 55, 0.12);
  }

  &:focus-visible{
    outline: 2px solid #E39737;
    outline-offset: 3px;
  }
}

.search-dialog__controls{
  padding: 0 clamp(1.25rem, 3vw, 2rem) 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);

  > p{
    margin: 0.55rem 0 0;
    color: rgb(120 117 115);
    font-size: 0.7rem;
  }
}

.search-dialog__input{
  width: 100%;
  min-height: 50px;

  :deep(.p-inputtext){
    font-size: 1rem;
  }
}

.search-dialog__results{
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 1rem clamp(1.25rem, 3vw, 2rem) 2rem;
}

.search-result-summary{
  display: flex;
  gap: 0.45rem;
  margin-bottom: 1rem;
  font-size: 0.82rem;

  span{
    color: rgb(120 117 115);
  }
}

.search-result-grid{
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  align-content: start;
}

.search-result-skeleton{
  padding: 0.75rem;
  display: grid;
  gap: 0.8rem;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: $public-radius-card;
  background: #191a1d;
}

.search-state{
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;

  > i{
    width: 54px;
    height: 54px;
    display: grid;
    place-items: center;
    border-radius: 999px;
    color: #E39737;
    background: rgba(227, 151, 55, 0.1);
    font-size: 1.25rem;
  }

  h3{
    margin: 1rem 0 0;
    font-size: 1.35rem;
  }

  p{
    max-width: 520px;
    margin: 0.55rem 0 0;
    color: rgb(168 162 158);
    line-height: 1.55;
  }

  a{
    margin-top: 1.2rem;
    display: inline-flex;
    gap: 0.5rem;
    align-items: center;
    color: #E39737;
    font-weight: 700;
    text-decoration: none;
  }

  :deep(.p-button){
    margin-top: 1.2rem;
  }
}

.search-state--error > i{
  color: #f87171;
  background: rgba(248, 113, 113, 0.1);
}

@media (max-width: 900px){
  .search-result-grid{
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 639px){
  .search-backdrop{
    padding: 0;
  }

  .search-dialog{
    width: 100%;
    height: 100dvh;
    border: 0;
    border-radius: 0;
  }

  .search-dialog__header{
    padding-top: 1rem;

    span{
      display: none;
    }
  }

  .search-close{
    width: 44px;
    height: 44px;
  }

  .search-dialog__controls{
    padding-bottom: 1rem;

    > p{
      line-height: 1.4;
    }
  }

  .search-dialog__results{
    padding-top: 1rem;
  }

  .search-result-grid{
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .search-result-skeleton{
    min-height: 124px;
    grid-template-columns: 112px minmax(0, 1fr);

    :deep(.p-skeleton:first-child){
      height: 100% !important;
      grid-row: span 3;
    }

  }

  .search-state{
    min-height: 330px;
  }
}
</style>
